import { safeError } from "./apiError";
import {
  getConsent,
  postDecisions,
  WEB_DEVICE_TOPICS,
  type ConsentOverview,
  type ConsentTopic,
  type DecisionIn,
  type Source,
  type TopicKey,
  type WebDeviceTopic,
} from "../api/consent";
import { useConsentStore } from "../store/consentStore";
import { toConsentLocale } from "./consentLocale";
import i18n from "../i18n";
import logger from "./logger";

// Session side of the privacy-consent gate (plan 2026-11-02, Task 18 + Amendment C): which consent dialog the
// signed-in account needs, the date-of-birth age check, and linking this device's answers to the account.

/** The topics that block the app until answered (terms ACCEPTED, privacy notice ACKNOWLEDGED). */
export const GATE_TOPICS: readonly TopicKey[] = ["terms", "privacy_notice"];

export const MIN_AGE = 14;

export const isGateTopic = (t: ConsentTopic) => GATE_TOPICS.includes(t.key);

export const isWebDeviceTopic = (t: ConsentTopic): t is ConsentTopic & { key: WebDeviceTopic } =>
  t.scope === "DEVICE" && (WEB_DEVICE_TOPICS as readonly string[]).includes(t.key);

/** A gate topic is satisfied by terms ACCEPTED / privacy notice ACKNOWLEDGED on the live version. */
function satisfied(t: ConsentTopic): boolean {
  if (t.needsAnswer) return false;
  return t.kind === "NOTICE" ? t.decision === "ACKNOWLEDGED" : t.decision === "ACCEPTED";
}

export type GateView =
  | { kind: "none" }
  /** First answer (or a declined terms): Before you start, existing-user variant. */
  | { kind: "before-you-start"; required: ConsentTopic[]; optional: ConsentTopic[] }
  /** A new material version of a topic answered before: only the changed topics in one list. */
  | { kind: "reconsent"; required: ConsentTopic[]; changed: ConsentTopic[] };

/**
 * What the signed-in account has to answer before the app. Re-consent when every unsatisfied gate topic was answered
 * before and only needs the new version; otherwise Before you start. The optional rows are the web device items this
 * browser has not answered (re-consent: those answered before whose new version needs an answer).
 */
export function gateView(overview: ConsentOverview | undefined): GateView {
  if (!overview?.consentPending) return { kind: "none" };
  const gate = overview.topics.filter(isGateTopic);
  const open = gate.filter((t) => !satisfied(t));
  if (open.length === 0) return { kind: "none" };
  const device = overview.topics.filter(isWebDeviceTopic).filter((t) => t.needsAnswer);
  const isUpdate = (t: ConsentTopic) => t.needsAnswer && t.decision !== null;
  if (open.every(isUpdate)) {
    return { kind: "reconsent", required: open, changed: device.filter((t) => t.decision !== null) };
  }
  return { kind: "before-you-start", required: open, optional: device };
}

/**
 * One batch for a gate dialog: terms ticked → ACCEPTED (unticked → DECLINED), the privacy notice ACKNOWLEDGED, the
 * optional device rows ACCEPTED when ticked, else DECLINED. Gate topics carry `gateSource`, device rows
 * `deviceSource` (new account: SIGN_UP for all; existing user: RECONSENT for the gate, FIRST_START for the device).
 */
export function gateDecisions(
  required: ConsentTopic[],
  optional: ConsentTopic[],
  ticked: ReadonlySet<TopicKey>,
  gateSource: Source,
  deviceSource: Source,
): DecisionIn[] {
  const clientAt = new Date().toISOString();
  const one = (t: ConsentTopic, source: Source): DecisionIn => ({
    versionId: t.versionId,
    textId: t.textId,
    decision: t.kind === "NOTICE" ? "ACKNOWLEDGED" : ticked.has(t.key) ? "ACCEPTED" : "DECLINED",
    source,
    clientAt,
  });
  return [...required.map((t) => one(t, gateSource)), ...optional.map((t) => one(t, deviceSource))];
}

/** Keeps the device rows of a recorded batch in the local store (Sentry / external media follow them). */
export function rememberDeviceAnswers(optional: ConsentTopic[], ticked: ReadonlySet<TopicKey>): void {
  const { setDevice } = useConsentStore.getState();
  for (const t of optional) {
    if (!isWebDeviceTopic(t)) continue;
    setDevice(t.key, { versionId: t.versionId, decision: ticked.has(t.key) ? "ACCEPTED" : "DECLINED" });
  }
}

let linking: Promise<void> | null = null;

/**
 * After any sign-in: this browser's stored device answers (storage banner) are sent once more as DEVICE_LINK, in one
 * batch, so the account carries them too. Only answers to the live version are sent (the server needs its text id);
 * nothing stored → no request. Never throws (logged); a second call while one runs shares it.
 */
export function linkDeviceAnswers(): Promise<void> {
  if (linking) return linking;
  const device = useConsentStore.getState().device;
  if (Object.keys(device).length === 0) return Promise.resolve();
  linking = (async () => {
    try {
      const overview = await getConsent(toConsentLocale(i18n.language));
      const clientAt = new Date().toISOString();
      const decisions: DecisionIn[] = [];
      for (const t of overview.topics.filter(isWebDeviceTopic)) {
        const mine = device[t.key];
        if (!mine || mine.versionId !== t.versionId) continue;
        decisions.push({
          versionId: t.versionId,
          textId: t.textId,
          decision: mine.decision,
          source: "DEVICE_LINK",
          clientAt,
        });
      }
      if (decisions.length > 0) await postDecisions(decisions);
    } catch (e) {
      logger.warn("[Consent] linking this device's answers failed", safeError(e));
    } finally {
      linking = null;
    }
  })();
  return linking;
}

// ── Date of birth ────────────────────────────────────────────────────────────────────────────────────────────────

export interface DayParts {
  year: number;
  month: number;
  day: number;
}

/** Today's calendar date in Spain (the server counts the age on the Europe/Madrid date). */
export function madridToday(now: Date = new Date()): DayParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
}

/** A real calendar date from 1900 up to today (Spain). */
export function isRealBirthDate(d: DayParts, today: DayParts = madridToday()): boolean {
  if (d.year < 1900 || d.month < 1 || d.month > 12 || d.day < 1) return false;
  const dt = new Date(Date.UTC(d.year, d.month - 1, d.day));
  if (dt.getUTCFullYear() !== d.year || dt.getUTCMonth() !== d.month - 1 || dt.getUTCDate() !== d.day) return false;
  return compare(d, today) <= 0;
}

function compare(a: DayParts, b: DayParts): number {
  return a.year - b.year || a.month - b.month || a.day - b.day;
}

/** Whole years on `today` (29 February birthdays count on 1 March in other years). */
export function ageOn(birth: DayParts, today: DayParts = madridToday()): number {
  let age = today.year - birth.year;
  if (today.month < birth.month || (today.month === birth.month && today.day < birth.day)) age -= 1;
  return age;
}

export function isUnder14(birth: DayParts, today: DayParts = madridToday()): boolean {
  return ageOn(birth, today) < MIN_AGE;
}

/** YYYY-MM-DD, the server's format. */
export function toIsoDate(d: DayParts): string {
  const p = (n: number, w: number) => String(n).padStart(w, "0");
  return `${p(d.year, 4)}-${p(d.month, 2)}-${p(d.day, 2)}`;
}
